// components/ui/HorizontalScroll.client.tsx
"use client";

import React, { useRef, useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export type HorizontalScrollClientProps = {
  scrollStep?: number;
  showControls?: boolean;
};

function HorizontalScrollClient({
  scrollStep = 300,
  showControls = true,
}: HorizontalScrollClientProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollButtons = () => {
    const container = containerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  };

  const scroll = (direction: "left" | "right") => {
    const container = containerRef.current;
    if (!container) return;

    const currentScroll = container.scrollLeft;
    const targetScroll =
      direction === "left" ? currentScroll - scrollStep : currentScroll + scrollStep;

    container.scrollTo({
      left: targetScroll,
      behavior: "smooth",
    });
  };

  const handleScroll = () => {
    updateScrollButtons();
  };

  useEffect(() => {
    // Find the scroll container by finding the parent horizontal-scroll-container
    if (wrapperRef.current) {
      const parent = wrapperRef.current.closest(".horizontal-scroll-container");
      if (parent) {
        const scrollContent = parent.querySelector(".horizontal-scroll-content") as HTMLDivElement;
        if (scrollContent) {
          containerRef.current = scrollContent;
          updateScrollButtons();

          scrollContent.addEventListener("scroll", handleScroll);
          const handleResize = () => {
            updateScrollButtons();
          };

          window.addEventListener("resize", handleResize);
          return () => {
            window.removeEventListener("resize", handleResize);
            scrollContent.removeEventListener("scroll", handleScroll);
          };
        }
      }
    }
  }, []);

  return (
    <div ref={wrapperRef}>
      {/* Desktop Controls */}
      {showControls && (
        <>
          <button
            onClick={() => scroll("left")}
            disabled={!canScrollLeft}
            className={`scroll-button scroll-button-left ${
              !canScrollLeft ? "scroll-button-disabled" : ""
            }`}
            aria-label="Scorri a sinistra"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={() => scroll("right")}
            disabled={!canScrollRight}
            className={`scroll-button scroll-button-right ${
              !canScrollRight ? "scroll-button-disabled" : ""
            }`}
            aria-label="Scorri a destra"
          >
            <ChevronRight size={20} />
          </button>
        </>
      )}

      {/* Fade Overlays */}
      <div
        className={`scroll-fade scroll-fade-left ${!canScrollLeft ? "scroll-fade-hidden" : ""}`}
      />
      <div
        className={`scroll-fade scroll-fade-right ${!canScrollRight ? "scroll-fade-hidden" : ""}`}
      />
    </div>
  );
}

export default HorizontalScrollClient;

